import React, {
  FC,
  useState,
  useMemo,
  memo,
  useCallback,
}                              from 'react';
import styled                  from 'styled-components';
import { ISearchParams }       from '@typings/search';
import {
  TDataTableRow,
  ISearchInputValues,
}                              from '@typings/components';
import { IBidderUser }         from '@typings/registeredBidders';
import { EAppPage }            from '@enums/common';
import { EBiddersPage }        from '@enums/registeredBidders';
import {
  parseRecords,
  getFormattedDate,
}                              from '@utils/commonFunctions';
import {
  ROWS_PER_PAGE,
  USER_STATUS_NAME,
}                              from '@utils/suggestions';
import { BIDDER_STATUS_COLOR } from '@modules/registeredBidders/utils/suggestions';
import { useFetchBiddersList } from '@modules/registeredBidders/queries/registeredBiddersQueries';
import { BiddersListPage }     from '@modules/registeredBidders/pages/BiddersListPage';

const BidderStatusText = styled.p`
  color: ${({ color }) => color || 'black'};
`;

const getUsersTableRows = (users: IBidderUser[] = []): TDataTableRow[] => users.map((user) => [{
  value : `${user.firstName} ${user.lastName}`,
  link  : `${EAppPage.RegisteredBidders}/${user.id}/${EBiddersPage.Details}`,
}, {
  value: (
    <BidderStatusText color={BIDDER_STATUS_COLOR[user.status]}>
      {USER_STATUS_NAME[user.status]}
    </BidderStatusText>
  ),
}, {
  value: `${user.city ? `${user.city},` : ''} ${user.state ?? ''}`.trim(),
}, {
  value : getFormattedDate(user.dateCreated, 'LLL'),
  align : 'center',
}]);

const BiddersList: FC = () => {
  const [searchText, setSearchText]   = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(0);

  const params = useMemo<ISearchParams>(
    () => {
      const textParams: ISearchParams = searchText
        ? {
          searchFields: ['FirstName', 'LastName'],
          searchText,
        }
        : {
          searchText: '*',
        };

      return {
        ...textParams,
        offset  : currentPage * ROWS_PER_PAGE,
        limit   : ROWS_PER_PAGE,
        orderBy : 'FirstName asc, LastName asc',
      };
    },
    [currentPage, searchText],
  );

  // #region API INTEGRATION
  const {
    data: biddersResponse,
    isLoading,
  } = useFetchBiddersList(params);
  // #endregion

  const onSearch = useCallback(
    ({ searchText: searchValue }: ISearchInputValues): void => {
      setSearchText(searchValue);
      setCurrentPage(0);
    },
    [],
  );

  const usersTableRows = useMemo<TDataTableRow[]>(
    () => {
      if (!biddersResponse) {
        return [];
      }

      const bidders: IBidderUser[] = parseRecords<IBidderUser>(biddersResponse);

      return getUsersTableRows(bidders);
    },
    [biddersResponse],
  );

  return (
    <BiddersListPage
      inputValue       = {searchText}
      tableRows        = {usersTableRows}
      isLoading        = {isLoading}
      totalUsersCount  = {biddersResponse?.totalRecordsFound ?? 0}
      currentPage      = {currentPage}
      rowsPerPage      = {ROWS_PER_PAGE}
      handleSearch     = {onSearch}
      handleChangePage = {(page) => setCurrentPage(page)}
    />
  );
};

export const BiddersListContainer = memo(BiddersList);
